import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Heart } from 'lucide-react';
import PropertyCard from './PropertyCard';
import BudgetPill, { readBudget } from './BudgetPill';
import { useMarket } from '../hooks/useMarket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem('lh_session_id');
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem('lh_session_id', sid);
    }
    return sid;
  } catch {
    return '';
  }
};

/**
 * "For your taste" row at the top of the listings page. Reuses the same
 * personalisation engine that drives the daily Home Crush email, so
 * rankings are consistent across surfaces. Falls back to a curated editorial
 * row (gold tier first) for new sessions with no reactions yet.
 *
 * Refreshes itself when a 😍/😐 reaction fires `lh:reaction-recorded`.
 */
const ForYourTasteRow = () => {
  const market = useMarket();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      const sid = getSessionId();
      const ub = readBudget();
      const params = { session_id: sid, limit: 4 };
      if (market.name) params.country = market.name;
      if (ub) {
        params.budget_min = ub.min;
        params.budget_max = ub.max;
      }
      axios
        .get(`${BACKEND_URL}/api/engagement/for-your-taste`, { params })
        .then((r) => !cancelled && setData(r.data))
        .catch(() => !cancelled && setData(null));
    };
    load();
    const handler = () => load();
    window.addEventListener('lh:reaction-recorded', handler);
    window.addEventListener('lh:budget-changed', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('lh:reaction-recorded', handler);
      window.removeEventListener('lh:budget-changed', handler);
    };
  }, [market.code, market.name]);

  if (!data || !data.listings || data.listings.length === 0) return null;

  const isPersonalised = data.reason === 'personalised';
  const reactions = data.reactions_seen || 0;

  return (
    <section
      className="bg-white border-b border-gray-100"
      data-testid="for-your-taste-row"
      data-reason={data.reason}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-5">
          <div>
            <div
              className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold mb-1 ${
                isPersonalised ? 'text-[#7042cc]' : 'text-amber-700'
              }`}
            >
              {isPersonalised ? (
                <Heart className="h-3 w-3 fill-[#7042cc]" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
              {isPersonalised ? 'For your taste' : 'Editor\u2019s pick'}
            </div>
            <h2
              className="text-2xl text-gray-900"
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 600 }}
            >
              {isPersonalised
                ? `Picked for you, based on ${reactions} reaction${reactions === 1 ? '' : 's'}`
                : 'Hand-picked homes to start with'}
            </h2>
            <BudgetPill budgetWindow={data.budget_window} />
            {!isPersonalised && (
              <p className="text-xs text-gray-500 mt-1">
                Tap the 😍 / 😐 buttons on the{' '}
                <a href="/home-crush" className="text-[#7042cc] hover:underline font-medium">
                  Daily Doorstep
                </a>{' '}
                and this row will quietly start matching your style.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {data.listings.map((p) => (
            <PropertyCard key={p._id} property={p} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ForYourTasteRow;
